import { useQuery } from "@tanstack/react-query";

import { bi } from "@/api/bi";
import { Card, CardBody, CardHeader, EmptyState } from "@/components/ui";

/**
 * Business-intelligence dashboard — AUM, customers, volume trend, and venue
 * mix. Read-only; data from the BI aggregation endpoints.
 */
export function AdminBiPage() {
  const overview = useQuery({ queryKey: ["bi-overview"], queryFn: () => bi.overview() });
  const volume = useQuery({ queryKey: ["bi-volume", 30], queryFn: () => bi.volume(30) });
  const venues = useQuery({ queryKey: ["bi-venues", 30], queryFn: () => bi.venues(30) });

  const maxNotional = Math.max(1, ...(volume.data ?? []).map((v) => v.notional));

  return (
    <div className="space-y-3">
      <h1 className="text-base font-semibold text-term-fg">Business intelligence</h1>

      <div className="grid gap-3 sm:grid-cols-3">
        <Kpi label="Customers" value={overview.data ? String(overview.data.customers) : "—"} />
        <Kpi label="Fills (24h)" value={overview.data ? String(overview.data.fills_24h) : "—"} />
        <Card>
          <CardBody>
            <div className="text-2xs uppercase tracking-wider text-term-mute">AUM</div>
            {overview.data && Object.keys(overview.data.aum_per_currency).length > 0 ? (
              <div className="mt-1 space-y-0.5">
                {Object.entries(overview.data.aum_per_currency).map(([ccy, amt]) => (
                  <div key={ccy} className="flex justify-between tnum text-sm text-term-fg">
                    <span className="text-term-dim">{ccy}</span>
                    <span>{amt.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-1 text-sm text-term-mute">—</div>
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <span className="text-sm font-medium">Volume (30d)</span>
        </CardHeader>
        <CardBody>
          {!volume.data || volume.data.length === 0 ? (
            <EmptyState title="No volume" description="Daily traded notional appears here." />
          ) : (
            <div className="space-y-1">
              {volume.data.map((v) => (
                <div key={v.day} className="flex items-center gap-2 text-2xs">
                  <span className="w-20 shrink-0 text-term-dim">{v.day}</span>
                  <div className="h-3 flex-1 overflow-hidden rounded-sm bg-term-elev">
                    <div
                      className="h-full bg-brand/60"
                      style={{ width: `${(v.notional / maxNotional) * 100}%` }}
                    />
                  </div>
                  <span className="w-24 shrink-0 text-right tnum text-term-dim">
                    {v.notional.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <span className="text-sm font-medium">Venue mix (30d)</span>
        </CardHeader>
        <CardBody className="p-0">
          {!venues.data || venues.data.length === 0 ? (
            <EmptyState title="No venue data" description="Routing/venue stats appear here." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Venue</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Fills</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Avg latency ms</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Avg slippage bps</th>
                </tr>
              </thead>
              <tbody>
                {venues.data.map((v) => (
                  <tr key={v.venue} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-mono text-2xs text-term-fg">{v.venue}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{v.fills}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">
                      {v.avg_latency_ms != null ? v.avg_latency_ms.toFixed(0) : "—"}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">
                      {v.avg_slippage_bps != null ? v.avg_slippage_bps.toFixed(2) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardBody>
        <div className="text-2xs uppercase tracking-wider text-term-mute">{label}</div>
        <div className="mt-1 text-xl font-semibold tnum text-term-fg">{value}</div>
      </CardBody>
    </Card>
  );
}
